import { normalizeCurrencyCode } from "../analysis/account-resolution.js"
import { parseDecimalCents } from "../analysis/money.js"
import type { ReconciliationVerdict } from "../analysis/reconciliation-verdict.js"
import type { StatementsAgentConfig } from "./schemas.js"
import type {
  AgentGateEvidence,
  StatementsGateName,
  StatementsVerdict,
} from "./vocabulary.js"

/**
 * The statements policy: one pure decision over evidence the product already
 * computed.
 *
 * The facts arrive as the same reconciliation verdict the document detail
 * renders, plus four workspace facts the Accounts surface already keeps:
 * which account the statement resolved to, what the previous period was,
 * whether another document covers the same period, and how many flags a
 * person still has to close. The evaluator adds nothing of its own. It
 * decides whether a statement can be trusted; it never moves money and it
 * never claims to detect a forgery.
 *
 * When the agent is not sure, it holds the statement and says why. Every
 * failed gate label is a complete sentence in flag-title grammar, because
 * those labels are the chips a person reads in the Inbox.
 */

/** The account this statement resolved to, as the resolution ladder left it. */
export type StatementAccountMatch =
  /** The workspace has an earlier statement for this account. */
  | "known"
  /** The account exists, but this is the first statement read for it. */
  | "new"
  /** No account resolved, or a second account carries the same identity. */
  | "ambiguous"

/** How this period sits against the account's previous one. */
export type StatementContinuity = {
  /** The end date of the newest earlier period, `YYYY-MM-DD`. */
  readonly previousPeriodEnd: string
  /**
   * Whole days of daylight between the two periods. 0 means this period
   * starts the day after the last one ended; a positive number is a gap and a
   * negative number is an overlap.
   */
  readonly gapDays: number
}

export type StatementsAgentFacts = {
  /** The same verdict the document detail route builds and the panel renders. */
  readonly reconciliation: ReconciliationVerdict
  readonly accountMatch: StatementAccountMatch
  /** Null when no earlier period exists to follow. */
  readonly continuity: StatementContinuity | null
  /**
   * Other live statements covering this account and period. Null when the
   * check could not run at all, which is a different answer from an empty
   * list: a statement that resolved to no account has no timeline to be a
   * duplicate within, and "no duplicate exists" would be a claim with
   * nothing behind it.
   */
  readonly duplicateDocumentIds: readonly string[] | null
  /**
   * Open flags a person must close before this document can be approved.
   *
   * The subset, not the total. A flag the pipeline itself marked as not
   * required carries no question and blocks no approval, so holding a
   * statement over one would ask for a click that changes nothing. This is
   * the same number the document list, the SDK, and the payables queue call
   * the open flag count.
   */
  readonly blockingFlagCount: number
}

const msPerDay = 86_400_000

/**
 * Parses the leading YYYY-MM-DD of an ISO date into a UTC epoch-ms day; null
 * when the shape is wrong. UTC only, because `new Date("2026-01-01")` in a
 * UTC-negative zone is the previous December, and a gap that moves with the
 * reader's timezone is not a number an accountant can cite.
 */
const parseUtcDay = (isoDate: string): number | null => {
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const time = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(time) ? null : time
}

/**
 * Days of daylight between the end of one period and the start of the next.
 * 0 when the new period starts the morning after the old one ended, which is
 * what a contiguous run of statements looks like. Null when either date is
 * unreadable, which the continuity gate reports as not checked.
 */
export const statementGapDays = (
  previousPeriodEnd: string,
  periodStart: string
): number | null => {
  const previous = parseUtcDay(previousPeriodEnd)
  const next = parseUtcDay(periodStart)

  if (previous === null || next === null) {
    return null
  }

  return Math.round((next - previous) / msPerDay) - 1
}

/**
 * "$412.60" for USD, "412.60 EUR" for everything else, "412.60" when the
 * statement never stated a currency we can read. Extraction sometimes emits
 * "$" or "US" in that field, and "412.60 US" is not a number anybody can
 * cite, so anything that is not an ISO code is treated as unstated.
 */
const money = (cents: number, currency: string | null): string => {
  const formatted = (Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const code = normalizeCurrencyCode(currency)

  if (code === null) return formatted
  return code === "USD" ? `$${formatted}` : `${formatted} ${code}`
}

const days = (count: number): string =>
  count === 1 ? "1 day" : `${count} days`

const gateEvidence = (
  gate: StatementsGateName,
  outcome: AgentGateEvidence["outcome"],
  label: string,
  expected: string | null,
  actual: string | null
): AgentGateEvidence => ({ gate, outcome, label, expected, actual })

/**
 * Balanced within the workspace's tolerance. An "off" verdict still passes
 * when the gap is inside the tolerance the admin set, because that is what
 * the control means; a verdict with no figures behind it holds, because a
 * statement nobody can check is not a statement anybody should trust.
 */
const reconciledGate = (
  facts: StatementsAgentFacts,
  config: StatementsAgentConfig
): AgentGateEvidence => {
  const { reconciliation } = facts
  const allowed = money(config.toleranceCents, reconciliation.currency)
  const differenceCents = parseDecimalCents(reconciliation.difference)

  if (reconciliation.status === "unknown") {
    return gateEvidence(
      "reconciled",
      "failed",
      "LedgerBox cannot verify the balances.",
      "an opening and a closing balance",
      "no balances on the statement"
    )
  }

  if (differenceCents === null) {
    return gateEvidence(
      "reconciled",
      "failed",
      "LedgerBox cannot verify the balances.",
      `a difference within ${allowed}`,
      "no difference to compare"
    )
  }

  const found = money(differenceCents, reconciliation.currency)

  if (Math.abs(differenceCents) > config.toleranceCents) {
    return gateEvidence(
      "reconciled",
      "failed",
      "The rows do not add up to the closing balance.",
      `a difference within ${allowed}`,
      found
    )
  }

  return gateEvidence(
    "reconciled",
    "passed",
    "The rows add up from the opening balance to the closing balance.",
    `a difference within ${allowed}`,
    found
  )
}

const knownAccountGate = (
  facts: StatementsAgentFacts,
  config: StatementsAgentConfig
): AgentGateEvidence => {
  if (!config.requireKnownAccount) {
    return gateEvidence(
      "known_account",
      "passed",
      "Known accounts only is off in your policy.",
      null,
      null
    )
  }
  if (facts.accountMatch === "known") {
    return gateEvidence(
      "known_account",
      "passed",
      "This account has an earlier statement.",
      "an earlier statement for this account",
      "found"
    )
  }
  if (facts.accountMatch === "new") {
    return gateEvidence(
      "known_account",
      "failed",
      "This is the first statement for this account.",
      "an earlier statement for this account",
      "none"
    )
  }

  return gateEvidence(
    "known_account",
    "failed",
    "LedgerBox cannot identify the account on this statement.",
    "one account for this statement",
    "no account, or more than one"
  )
}

/**
 * The period follows the account's last one. A first statement has nothing to
 * follow, so the gate reports not checked rather than failing: whether a
 * first statement may be approved is what the known-account control decides.
 */
const continuityGate = (facts: StatementsAgentFacts): AgentGateEvidence => {
  const { continuity } = facts

  if (continuity === null) {
    return gateEvidence(
      "continuity",
      "not_available",
      "There is no earlier statement for this account.",
      null,
      null
    )
  }

  const expected = `the day after ${continuity.previousPeriodEnd}`

  if (continuity.gapDays > 0) {
    return gateEvidence(
      "continuity",
      "failed",
      "This statement leaves a gap after the previous statement.",
      expected,
      `${days(continuity.gapDays)} later`
    )
  }
  if (continuity.gapDays < 0) {
    return gateEvidence(
      "continuity",
      "failed",
      "This statement overlaps the previous statement.",
      expected,
      `${days(Math.abs(continuity.gapDays))} earlier`
    )
  }

  return gateEvidence(
    "continuity",
    "passed",
    "This statement starts the day after the previous statement ended.",
    expected,
    "the day after"
  )
}

const duplicateGate = (facts: StatementsAgentFacts): AgentGateEvidence => {
  if (facts.duplicateDocumentIds === null) {
    return gateEvidence(
      "duplicate",
      "not_available",
      "LedgerBox cannot tell which account and period this statement covers.",
      null,
      null
    )
  }

  const count = facts.duplicateDocumentIds.length

  if (count > 0) {
    return gateEvidence(
      "duplicate",
      "failed",
      "Another statement covers this account and period.",
      "no other statement for this period",
      count === 1 ? "1 other statement" : `${count} other statements`
    )
  }

  return gateEvidence(
    "duplicate",
    "passed",
    "No other statement covers this account and period.",
    "no other statement for this period",
    "none"
  )
}

const cleanFlagsGate = (facts: StatementsAgentFacts): AgentGateEvidence => {
  const count = facts.blockingFlagCount

  if (count > 0) {
    return gateEvidence(
      "clean_flags",
      "failed",
      "LedgerBox flagged this statement in review.",
      "no required flags open",
      count === 1 ? "1 required flag open" : `${count} required flags open`
    )
  }

  return gateEvidence(
    "clean_flags",
    "passed",
    "No required flags are open.",
    "no required flags open",
    "none"
  )
}

/**
 * `(facts, config) -> verdict`. Approves only when every gate passes; holds
 * with the failed gates' sentences otherwise. `reasons` always carries all
 * five gates, passed ones included, because "all 5 gates passed" has to be
 * able to show its work.
 */
export const evaluateStatementsPolicy = (
  facts: StatementsAgentFacts,
  config: StatementsAgentConfig
): StatementsVerdict => {
  const reasons: readonly AgentGateEvidence[] = [
    reconciledGate(facts, config),
    knownAccountGate(facts, config),
    continuityGate(facts),
    duplicateGate(facts),
    cleanFlagsGate(facts),
  ]

  return {
    action: reasons.some((entry) => entry.outcome === "failed")
      ? "hold"
      : "approve",
    reasons,
  }
}

/**
 * The verdict for a statement the pipeline produced no summary for at all,
 * so there are no figures for any gate to read. Held on the reconciled gate;
 * every other gate had nothing to run against.
 *
 * A statement that is merely missing a period is not this: its balances may
 * still chain, and saying they cannot be verified would contradict the
 * verdict the document detail shows.
 */
export const evaluateUnreadableStatement = (): StatementsVerdict => ({
  action: "hold",
  reasons: [
    gateEvidence(
      "reconciled",
      "failed",
      "LedgerBox cannot verify the balances.",
      "an opening and a closing balance",
      "no balances on the statement"
    ),
    gateEvidence("known_account", "not_available", "This check did not run.", null, null),
    gateEvidence("continuity", "not_available", "This check did not run.", null, null),
    gateEvidence("duplicate", "not_available", "This check did not run.", null, null),
    gateEvidence("clean_flags", "not_available", "This check did not run.", null, null),
  ],
})
